/**
 * Bảng cấu hình khối đang chọn (cột phải trình dựng). Mọi khoá ghi vào config
 * đều là khoá backend đọc (automation-actions.ts) — xem catalog.ts.
 */
import { ManageStagesLink } from '@/components/shared/manage-links'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { useId, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Braces, Copy, Info, Plus, RefreshCw, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LIFECYCLE_STAGES, STAGE_LABELS } from '@/hooks/use-contacts'
import { useCdpProperties } from '@/hooks/use-cdp'
import { useOrgMembers } from '@/hooks/use-settings'
import { useOaAccounts, useZnsTemplates } from '@/hooks/use-zns'
import { useChienDichKetBan } from '@/hooks/use-zalo-friend-campaigns'
import { useChienDichNhanTin } from '@/hooks/use-zalo-broadcast-campaigns'
import { useMessageTemplates, useRotateWebhookToken, useWebhookToken } from '@/hooks/use-automation'
import {
  ACTION_CHON_DUOC, DON_VI_CHO, DON_VI_CHO_LABEL, NGUOI_NHAN_THONG_BAO, NGUONG_IM_LANG, TOAN_TU, TOAN_TU_KHONG_GIA_TRI, TOAN_TU_LABEL,
  actionMeta, bienNoiDung, triggerMeta, truongDieuKien, type LuatDieuKien, type TruongDuLieu,
} from '@/lib/automation/catalog'
import type { BlockData, BlockNode } from '@/lib/automation/flow-adapter'
import type { BuocChay } from '@/lib/automation/run-result'
import { WEEKDAYS, scheduleCron, scheduleSummary, tanSuat } from '@/lib/automation/lich-chay'
import { BieuTuongBuoc, KHOI_META } from './khoi-canvas'
import { currentLocale } from '@/lib/i18n'

type Cfg = Record<string, unknown>
type SetCfg = (patch: Cfg) => void

interface Props {
  node: BlockNode
  triggerType: string
  ruleId?: string
  /** Trường `trigger.*` lấy từ mẫu payload webhook gần nhất + dữ liệu động theo kết nối (Evotech…) tổ chức đã có. */
  truongMoRong: TruongDuLieu[]
  chiXem: boolean
  buocGanNhat?: BuocChay
  capNhat: (patch: Partial<BlockData>) => void
  doiKichHoat: () => void
  xoa: () => void
  dong: () => void
}

export function CauHinhKhoi({ node, triggerType, ruleId, truongMoRong, chiXem, buocGanNhat, capNhat, doiKichHoat, xoa, dong }: Props) {
  const { t } = useTranslation('autoConfig')
  const d = node.data
  const setCfg: SetCfg = (patch) => capNhat({ config: { ...d.config, ...patch } })
  const bien = useMemo(() => bienNoiDung(triggerType, truongMoRong), [triggerType, truongMoRong])

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-center gap-2">
        <Badge variant="outline">{KHOI_META[d.kind].nhan}</Badge>
        {chiXem && <Badge variant="secondary">{t('block.viewOnly')}</Badge>}
        <div className="ml-auto flex items-center gap-1">
          {d.kind !== 'source' && !chiXem && <Button size="sm" variant="ghost" className="text-destructive" onClick={xoa} title={t('block.delete')}><Trash2 className="h-3.5 w-3.5" /></Button>}
          <Button size="sm" variant="ghost" onClick={dong} title={t('block.closePanel')}><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Chế độ chỉ xem: khoá mọi ô nhập bằng fieldset (nút đóng nằm ngoài). */}
      <fieldset disabled={chiXem} className="min-w-0 space-y-3">
      <div><Label className="text-xs">{t('block.name')}</Label><Input value={d.label} onChange={(e) => capNhat({ label: e.target.value })} className="h-8" /></div>

      {d.kind !== 'source' && d.kind !== 'note' && (
        <div><Label className="text-xs">{t('block.status')}</Label>
          <Select value={d.status} onValueChange={(v) => capNhat({ status: v as BlockData['status'] })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t('block.statusActive')}</SelectItem>
              <SelectItem value="wip">{t('block.statusWip')}</SelectItem>
              <SelectItem value="disabled">{t('block.statusDisabled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {d.kind === 'source' && <CauHinhKichHoat d={d} ruleId={ruleId} setCfg={setCfg} doiKichHoat={doiKichHoat} chiXem={chiXem} truong={truongDieuKien(triggerType, truongMoRong)} />}
      {d.kind === 'condition' && <CauHinhDieuKien cfg={d.config} set={setCfg} truong={truongDieuKien(triggerType, truongMoRong)} />}
      {d.kind === 'delay' && <CauHinhCho cfg={d.config} set={setCfg} />}
      {d.kind === 'action' && <CauHinhHanhDong d={d} capNhat={capNhat} set={setCfg} bien={bien} triggerType={triggerType} />}
      {d.kind === 'note' && <div><Label className="text-xs">{t('block.noteText')}</Label><Textarea rows={4} value={String(d.config.text ?? '')} onChange={(e) => setCfg({ text: e.target.value })} className="text-xs" /></div>}

      {buocGanNhat && (
        <div className="rounded-md border bg-muted/30 p-2 text-[11px]">
          <p className="mb-1 flex items-center gap-1 font-medium"><BieuTuongBuoc status={buocGanNhat.status} className="h-3.5 w-3.5" /> {t('block.lastTestRun')}</p>
          <p className="whitespace-pre-wrap text-muted-foreground">{buocGanNhat.tomTat}</p>
          {buocGanNhat.output && <pre className="mt-1 max-h-40 overflow-auto rounded bg-background p-1.5">{JSON.stringify(buocGanNhat.output, null, 2)}</pre>}
        </div>
      )}
      </fieldset>
    </div>
  )
}

// ── Kích hoạt ────────────────────────────────────────────────────────
function CauHinhKichHoat({ d, ruleId, setCfg, doiKichHoat, chiXem, truong }: { d: BlockData; ruleId?: string; setCfg: SetCfg; doiKichHoat: () => void; chiXem: boolean; truong: TruongDuLieu[] }) {
  const { t } = useTranslation('autoConfig')
  const m = triggerMeta(d.loai)
  return (
    <>
      <div className="rounded-md border p-2.5">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold">{m.ten}</p>
            <p className="text-[11px] text-muted-foreground">{m.moTa}</p>
          </div>
          {!chiXem && <Button size="sm" variant="outline" className="h-7 shrink-0 text-[11px]" onClick={doiKichHoat}><RefreshCw className="h-3 w-3" /> {t('common:actions.change')}</Button>}
        </div>
        {m.khongCoNguonPhat && <p className="mt-2 flex gap-1 text-[11px] text-warning"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {t('trigger.noSource')}</p>}
        {!m.coKhach && <p className="mt-2 flex gap-1 text-[11px] text-muted-foreground"><Info className="mt-px h-3.5 w-3.5 shrink-0" /> {d.loai === 'webhook' ? t('trigger.noContactWebhook') : t('trigger.noContactContext')}</p>}
      </div>
      {d.loai === 'schedule' && <ScheduleConfig config={d.config} onConfig={setCfg} />}
      {d.loai === 'webhook' && <WebhookConfig ruleId={ruleId} config={d.config} onConfig={setCfg} />}
      {d.loai === 'manual' && (
        <div className="space-y-1">
          <Label className="text-xs">{t('trigger.buttonLabel')}</Label>
          <Input value={String(d.config.buttonLabel ?? '')} onChange={(e) => setCfg({ buttonLabel: e.target.value })} className="h-8" placeholder={t('trigger.buttonPlaceholder')} />
          <p className="text-[11px] text-muted-foreground">{t('trigger.buttonHint')}</p>
        </div>
      )}
      {d.loai === 'conversation_idle' && (
        <div><Label className="text-xs">{t('trigger.idle')}</Label>
          <Select value={String(d.config.threshold ?? '30m')} onValueChange={(v) => setCfg({ threshold: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{NGUONG_IM_LANG.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2 rounded-md border p-2">
        <div>
          <Label className="text-xs">{t('trigger.filterTitle')}</Label>
          <p className="text-[10.5px] text-muted-foreground">{t('trigger.filterHint')}</p>
        </div>
        <BoLuat rules={(d.config.filterRules as LuatDieuKien[] | undefined) ?? []} logic={d.config.filterLogic} truong={truong}
          onChange={(p) => setCfg({ ...(p.rules ? { filterRules: p.rules } : {}), ...(p.logic ? { filterLogic: p.logic } : {}) })}
          luatMoi={{ field: truong.find((f) => f.path.startsWith('trigger.'))?.path ?? 'contact.lifecycleStage', op: 'eq', value: '' }} />
      </div>
      {m.truong.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">{t('trigger.carried')}</Label>
          <div className="flex flex-wrap gap-1">{m.truong.map((f) => <code key={f.path} title={f.label} className="rounded bg-muted px-1.5 py-0.5 text-[10.5px]">{f.path}</code>)}</div>
        </div>
      )}
    </>
  )
}

/** Cấu hình lịch chạy: chọn tần suất rồi khai giờ/thứ/ngày tương ứng. */
function ScheduleConfig({ config, onConfig }: { config: Cfg; onConfig: SetCfg }) {
  const { t } = useTranslation('autoConfig')
  const freq = tanSuat(config)
  const atTime = String(config.atTime || '09:00')
  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
      <div className="space-y-1">
        <Label className="text-xs">{t('schedule.frequency')}</Label>
        <Select value={freq || 'daily'} onValueChange={(v) => onConfig({ frequency: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="minutes">{t('schedule.minutes')}</SelectItem>
            <SelectItem value="hourly">{t('schedule.hourly')}</SelectItem>
            <SelectItem value="daily">{t('schedule.daily')}</SelectItem>
            <SelectItem value="weekly">{t('schedule.weekly')}</SelectItem>
            <SelectItem value="monthly">{t('schedule.monthly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {freq === 'minutes' && (
        <div className="space-y-1"><Label className="text-xs">{t('schedule.everyMinutes')}</Label>
          <Input type="number" min={1} className="h-8" value={Number(config.everyMinutes) || 5} onChange={(e) => onConfig({ everyMinutes: Math.max(1, Number(e.target.value) || 1) })} />
        </div>
      )}
      {freq === 'hourly' && (
        <div className="space-y-1"><Label className="text-xs">{t('schedule.atMinute')}</Label>
          <Input type="number" min={0} max={59} className="h-8" value={Number(config.minute) || 0} onChange={(e) => onConfig({ minute: Math.min(59, Math.max(0, Number(e.target.value) || 0)) })} />
        </div>
      )}
      {freq === 'weekly' && (
        <div className="space-y-1"><Label className="text-xs">{t('schedule.onWeekday')}</Label>
          <Select value={String(Number(config.weekday ?? 1))} onValueChange={(v) => onConfig({ weekday: Number(v) })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {freq === 'monthly' && (
        <div className="space-y-1"><Label className="text-xs">{t('schedule.onDay')}</Label>
          <Input type="number" min={1} max={31} className="h-8" value={Number(config.dayOfMonth) || 1} onChange={(e) => onConfig({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })} />
          {Number(config.dayOfMonth) > 28 && <p className="text-[11px] text-warning">{t('schedule.dayWarn')}</p>}
        </div>
      )}
      {(freq === 'daily' || freq === 'weekly' || freq === 'monthly' || !freq) && (
        <div className="space-y-1"><Label className="text-xs">{t('schedule.atTime')}</Label><Input type="time" className="h-8" value={atTime} onChange={(e) => onConfig({ atTime: e.target.value })} /></div>
      )}
      <div className="space-y-0.5 border-t pt-2">
        <p className="text-xs font-medium">{scheduleSummary(config)}</p>
        <p className="font-mono text-[11px] text-muted-foreground">{t('schedule.cron', { cron: scheduleCron(config) })}</p>
        <p className="text-[11px] text-muted-foreground">{t('schedule.timezone')}</p>
      </div>
    </div>
  )
}

/** URL webhook (tạo token nếu chưa có) + sao chép/xoay + các trường nhận được + giới hạn IP. */
function WebhookConfig({ ruleId, config, onConfig }: { ruleId?: string; config: Cfg; onConfig: SetCfg }) {
  const { t } = useTranslation('autoConfig')
  const { data, isLoading, isError } = useWebhookToken(ruleId, !!ruleId)
  const rotate = useRotateWebhookToken(ruleId)
  const sample = (rotate.data?.lastPayload ?? data?.lastPayload) as Record<string, unknown> | null | undefined
  const receivedAt = rotate.data?.lastReceivedAt ?? data?.lastReceivedAt
  if (!ruleId) return <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">{t('webhook.saveFirst')}</div>
  const url = rotate.data?.url ?? data?.url ?? ''
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground"><Trans t={t} i18nKey="webhook.intro" components={{ c: <code /> }} /></p>
      {isLoading ? <p className="text-xs text-muted-foreground">{t('webhook.loading')}</p>
        : isError ? <p className="text-xs text-destructive">{t('webhook.loadError')}</p>
        : (
          <div className="flex items-center gap-1.5">
            <Input readOnly value={url} className="h-8 text-xs" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" title={t('common:actions.copy')} onClick={() => { navigator.clipboard.writeText(url); toast.success(t('webhook.copied')) }}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      <Button variant="ghost" size="sm" className="text-xs" disabled={rotate.isPending}
        onClick={() => { if (confirm(t('webhook.rotateConfirm'))) rotate.mutate(undefined, { onSuccess: () => toast.success(t('webhook.rotated')) }) }}>
        {t('webhook.rotate')}
      </Button>
      <div className="space-y-1 border-t pt-2">
        <Label className="text-xs">{t('webhook.fields')}</Label>
        {sample && Object.keys(sample).length > 0 ? (
          <>
            <div className="flex flex-wrap gap-1">{Object.keys(sample).slice(0, 24).map((k) => <code key={k} className="rounded bg-background px-1.5 py-0.5 text-[11px]">trigger.{k}</code>)}</div>
            <p className="text-[11px] text-muted-foreground">{receivedAt ? t('webhook.fieldsHintAt', { example: '{{trigger.' + Object.keys(sample)[0] + '}}', time: new Date(receivedAt).toLocaleString(currentLocale()) }) : t('webhook.fieldsHint', { example: '{{trigger.' + Object.keys(sample)[0] + '}}' })}</p>
          </>
        ) : <p className="text-[11px] text-muted-foreground">{t('webhook.noRequest')}</p>}
      </div>
      <div className="space-y-1 border-t pt-2">
        <Label className="text-xs">{t('webhook.ipLimit')}</Label>
        <Textarea rows={2} className="text-xs" placeholder={t('webhook.ipPlaceholder')} value={String(config.allowedIps ?? '')} onChange={(e) => onConfig({ allowedIps: e.target.value })} />
      </div>
    </div>
  )
}

// ── Điều kiện ────────────────────────────────────────────────────────
/** Trình soạn một bộ luật {field, op, value} + VÀ/HOẶC — dùng cho khối Điều kiện và lọc "Chỉ khi". */
function BoLuat({ rules, logic, onChange, truong, luatMoi }: {
  rules: LuatDieuKien[]; logic: unknown; truong: TruongDuLieu[]
  onChange: (p: { rules?: LuatDieuKien[]; logic?: 'and' | 'or' }) => void
  luatMoi: LuatDieuKien
}) {
  const { t } = useTranslation('autoConfig')
  const listId = useId()
  const sua = (i: number, p: Partial<LuatDieuKien>) => onChange({ rules: rules.map((r, j) => (j === i ? { ...r, ...p } : r)) })
  return (
    <>
      {rules.length > 1 && (
        <div className="flex items-center gap-2 text-xs"><span>{t('rules.combine')}</span>
          <Switch checked={logic === 'or'} onCheckedChange={(v) => onChange({ logic: v ? 'or' : 'and' })} /><span>{logic === 'or' ? t('rules.or') : t('rules.and')}</span>
        </div>
      )}
      <datalist id={listId}>{truong.map((f) => <option key={f.path} value={f.path}>{f.label}</option>)}</datalist>
      {rules.map((r, i) => (
        <div key={i} className="space-y-1 rounded-md border p-2">
          <Input list={listId} value={r.field} onChange={(e) => sua(i, { field: e.target.value })} className="h-8 font-mono text-xs" placeholder="contact.lifecycleStage" />
          <p className="truncate text-[10.5px] text-muted-foreground">{truong.find((f) => f.path === r.field)?.label ?? t('rules.fieldHint')}</p>
          <div className="flex gap-1">
            <Select value={r.op} onValueChange={(v) => sua(i, { op: v })}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{TOAN_TU.map((op) => <SelectItem key={op} value={op}>{TOAN_TU_LABEL[op]}</SelectItem>)}</SelectContent>
            </Select>
            {!TOAN_TU_KHONG_GIA_TRI.has(r.op) && <Input value={r.value} onChange={(e) => sua(i, { value: e.target.value })} className="h-8 text-xs" placeholder={t('rules.value')} />}
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={() => onChange({ rules: rules.filter((_, j) => j !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange({ rules: [...rules, luatMoi] })}><Plus className="h-3.5 w-3.5" /> {t('rules.add')}</Button>
    </>
  )
}

function CauHinhDieuKien({ cfg, set, truong }: { cfg: Cfg; set: SetCfg; truong: TruongDuLieu[] }) {
  const { t } = useTranslation('autoConfig')
  return (
    <>
      <BoLuat rules={(cfg.rules as LuatDieuKien[] | undefined) ?? []} logic={cfg.logic} truong={truong}
        onChange={(p) => set(p)} luatMoi={{ field: 'contact.lifecycleStage', op: 'eq', value: '' }} />
      <p className="text-[11px] text-muted-foreground">{t('rules.note')}</p>
    </>
  )
}

// ── Chờ ──────────────────────────────────────────────────────────────
function CauHinhCho({ cfg, set }: { cfg: Cfg; set: SetCfg }) {
  const { t } = useTranslation('autoConfig')
  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <div className="flex-1"><Label className="text-xs">{t('delay.time')}</Label><Input type="number" min={0} value={String(cfg.duration ?? 1)} onChange={(e) => set({ duration: Math.max(0, Number(e.target.value) || 0) })} className="h-8" /></div>
        <div className="flex-1"><Label className="text-xs">{t('delay.unit')}</Label>
          <Select value={String(cfg.unit ?? 'hours')} onValueChange={(v) => set({ unit: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{DON_VI_CHO.map((u) => <SelectItem key={u} value={u}>{DON_VI_CHO_LABEL[u]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">{t('delay.note')}</p>
    </div>
  )
}

// ── Ô văn bản có chèn biến {{…}} ─────────────────────────────────────
function OVanBan({ nhan, value, onChange, bien, rows = 4, goiY }: { nhan: string; value: string; onChange: (v: string) => void; bien: TruongDuLieu[]; rows?: number; goiY?: string }) {
  const { t } = useTranslation('autoConfig')
  const [mo, setMo] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const chen = (path: string) => {
    const el = ref.current
    const token = `{{${path}}}`
    if (!el) { onChange(value + token); return }
    const a = el.selectionStart ?? value.length, b = el.selectionEnd ?? value.length
    onChange(value.slice(0, a) + token + value.slice(b))
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + token.length, a + token.length) })
  }
  return (
    <div>
      <div className="flex items-center justify-between"><Label className="text-xs">{nhan}</Label><button type="button" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" onClick={() => setMo((v) => !v)}><Braces className="h-3 w-3" /> {t('text.insertVar')}</button></div>
      {mo && <div className="mb-1 flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded-md border p-1.5">{bien.map((f) => <button key={f.path} type="button" title={f.label} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10.5px] hover:bg-accent" onClick={() => chen(f.path)}>{f.path}</button>)}</div>}
      <Textarea ref={ref} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="text-xs" />
      {goiY && <p className="mt-0.5 text-[10.5px] text-muted-foreground">{goiY}</p>}
    </div>
  )
}

// ── Hành động ────────────────────────────────────────────────────────
function CauHinhHanhDong({ d, capNhat, set, bien, triggerType }: { d: BlockData; capNhat: (p: Partial<BlockData>) => void; set: SetCfg; bien: TruongDuLieu[]; triggerType: string }) {
  const { t } = useTranslation('autoConfig')
  const lifecycleStages = useLifecycleStages()
  const type = d.loai ?? ''
  const cfg = d.config
  const S = (k: string) => String(cfg[k] ?? '')
  const meta = actionMeta(type)
  const tm = triggerMeta(triggerType)
  const doiLoai = (moi: string) => {
    // Đổi loại → cấu hình mặc định ĐÚNG khoá của loại mới; nhãn mặc định đổi theo.
    const nhanCu = actionMeta(type).ten
    capNhat({ loai: moi, config: { ...actionMeta(moi).macDinh }, ...(d.label === nhanCu || !d.label ? { label: actionMeta(moi).ten } : {}) })
  }
  const loaiChon = ACTION_CHON_DUOC.includes(type) || !type ? ACTION_CHON_DUOC : [type, ...ACTION_CHON_DUOC]

  return (
    <>
      <div><Label className="text-xs">{t('action.type')}</Label>
        <Select value={type} onValueChange={doiLoai}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('action.choose')} /></SelectTrigger>
          <SelectContent>{loaiChon.map((a) => <SelectItem key={a} value={a}>{actionMeta(a).ten}</SelectItem>)}</SelectContent>
        </Select>
        {meta.moTa && <p className="mt-1 text-[11px] text-muted-foreground">{meta.moTa}</p>}
      </div>

      {meta.sapCo && <p className="flex gap-1 rounded-md border border-warning/50 bg-warning/10 p-2 text-[11px]"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-warning" /> {t('action.comingSoon')}</p>}
      {meta.cu && <p className="flex gap-1 rounded-md border p-2 text-[11px] text-muted-foreground"><Info className="mt-px h-3.5 w-3.5 shrink-0" /> {t('action.legacy')}</p>}
      {meta.canHoiThoai && !tm.coHoiThoai && (
        <p className="flex gap-1 text-[11px] text-muted-foreground"><Info className="mt-px h-3.5 w-3.5 shrink-0" /> {t('action.noConversation', { name: tm.ten })}</p>
      )}

      {type === 'send_message' && <OVanBan nhan={t('action.messageText')} value={S('text')} onChange={(v) => set({ text: v })} bien={bien} goiY={t('action.messageHint')} />}
      {type === 'send_template' && <ChonMauTin value={S('templateId')} onChange={(v) => set({ templateId: v })} />}
      {type === 'send_zalo_zns' && <CauHinhZns cfg={cfg} set={set} bien={bien} />}
      {(type === 'add_tag' || type === 'remove_tag') && <div><Label className="text-xs">{t('action.tag')}</Label><Input value={S('tag')} onChange={(e) => set({ tag: e.target.value })} className="h-8" placeholder="VIP" /></div>}
      {(type === 'update_lifecycle' || type === 'change_status') && (
        <div><Label className="text-xs">{t('action.toStage')}</Label>
          <Select value={S('lifecycleStage') || 'lead'} onValueChange={(v) => set({ lifecycleStage: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{lifecycleStages.stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}<ManageStagesLink /></SelectContent>
          </Select>
        </div>
      )}
      {type === 'update_property' && (
        <>
          <ChonThuocTinh value={S('fieldKey') || S('key')} onChange={(v) => set({ fieldKey: v, key: undefined })} />
          <OVanBan nhan={t('action.value')} value={S('value')} onChange={(v) => set({ value: v })} bien={bien} rows={2} goiY={t('action.nowHint')} />
        </>
      )}
      {type === 'increment_property' && (
        <>
          <ChonThuocTinh value={S('fieldKey') || S('key')} onChange={(v) => set({ fieldKey: v, key: undefined })} chiSo />
          <div><Label className="text-xs">{t('action.increment')}</Label><Input type="number" value={String(cfg.amount ?? cfg.value ?? 1)} onChange={(e) => set({ amount: Number(e.target.value), value: undefined })} className="h-8" /></div>
        </>
      )}
      {type === 'track_event' && (
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">{t('action.eventName')}</Label><Input value={S('eventName')} onChange={(e) => set({ eventName: e.target.value })} className="h-8 font-mono text-xs" placeholder="dang_ky_tu_van" /></div>
          <div><Label className="text-xs">{t('action.eventValue')}</Label><Input value={S('eventValue')} onChange={(e) => set({ eventValue: e.target.value })} className="h-8 text-xs" /></div>
        </div>
      )}
      {type === 'assign_agent' && <ChonNhanVien value={S('userId')} onChange={(v) => set({ userId: v })} />}
      {type === 'create_appointment' && (
        <>
          <div><Label className="text-xs">{t('action.apptTitle')}</Label><Input value={S('title')} onChange={(e) => set({ title: e.target.value })} className="h-8" placeholder={t('action.apptPlaceholder')} /></div>
          <div><Label className="text-xs">{t('action.apptAfter')}</Label><Input type="number" min={0} value={String(cfg.offsetHours ?? 24)} onChange={(e) => set({ offsetHours: Math.max(0, Number(e.target.value) || 0) })} className="h-8" /></div>
          <p className="text-[11px] text-muted-foreground">{t('action.apptNote')}</p>
        </>
      )}
      {type === 'send_notification' && (
        <>
          <div><Label className="text-xs">{t('action.notifyTo')}</Label>
            <Select value={S('recipients') || 'assignee'} onValueChange={(v) => set({ recipients: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(NGUOI_NHAN_THONG_BAO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {S('recipients') === 'users' && <ChonNhieuNhanVien value={(cfg.userIds as string[] | undefined) ?? []} onChange={(v) => set({ userIds: v })} />}
          <OVanBan nhan={t('common:fields.title')} value={S('title')} onChange={(v) => set({ title: v })} bien={bien} rows={1} />
          <OVanBan nhan={t('common:fields.content')} value={S('message')} onChange={(v) => set({ message: v })} bien={bien} rows={3} goiY={t('action.notifyHint')} />
        </>
      )}
      {type === 'http_request' && (
        <>
          <div className="grid grid-cols-[6.5rem_1fr] gap-2">
            <div><Label className="text-xs">{t('action.method')}</Label>
              <Select value={S('method') || 'POST'} onValueChange={(v) => set({ method: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <OVanBan nhan="URL" value={S('url')} onChange={(v) => set({ url: v })} bien={bien} rows={1} />
          </div>
          <div><Label className="text-xs">{t('action.headers')}</Label><Textarea rows={2} value={S('headers')} onChange={(e) => set({ headers: e.target.value })} className="font-mono text-xs" placeholder="Authorization: Bearer …" /></div>
          {!['GET', 'DELETE'].includes(S('method') || 'POST') && <OVanBan nhan="Body" value={S('body')} onChange={(v) => set({ body: v })} bien={bien} rows={4} goiY={t('action.bodyHint')} />}
          <p className="text-[11px] text-muted-foreground">{t('action.httpNote')}</p>
        </>
      )}
      {type === 'send_friend_request' && <ChonChienDichKetBan value={S('campaignId')} onChange={(v) => set({ campaignId: v })} />}
      {type === 'add_to_broadcast_campaign' && <ChonChienDichNhanTin value={S('campaignId')} onChange={(v) => set({ campaignId: v })} />}
      {type === 'ai_cdp' && <p className="text-[11px] text-muted-foreground">{t('action.aiCdp')}</p>}
    </>
  )
}

function ChonMauTin({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('autoConfig')
  const { data: templates, isLoading } = useMessageTemplates()
  const tpl = templates?.find((x) => x.id === value)
  return (
    <div className="space-y-1">
      <Label className="text-xs">{t('tpl.label')}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={isLoading ? t('shared.loading') : t('tpl.choose')} /></SelectTrigger>
        <SelectContent>{(templates ?? []).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}{x.isPersonal ? ` ${t('tpl.personal')}` : ''}</SelectItem>)}</SelectContent>
      </Select>
      {templates && templates.length === 0 && <p className="text-[11px] text-muted-foreground">{t('tpl.none')}</p>}
      {tpl && <p className="whitespace-pre-wrap rounded-md border bg-muted/40 p-2 text-[11px]">{tpl.content}</p>}
    </div>
  )
}

/** Chọn một nhân viên phụ trách. */
function ChonNhanVien({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('autoConfig')
  const { data: members } = useOrgMembers()
  return (
    <div><Label className="text-xs">{t('staff.label')}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('staff.choose')} /></SelectTrigger>
        <SelectContent>
          {(members ?? []).map((m) => <SelectItem key={m.id} value={m.id}>{m.fullName ?? m.id}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

function ChonChienDichKetBan({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('autoConfig')
  const { data, isLoading } = useChienDichKetBan()
  const conDung = (data ?? []).filter((c) => (c.status !== 'completed' && c.status !== 'cancelled') || c.id === value)
  const c = data?.find((x) => x.id === value)
  return (
    <div className="space-y-1">
      <Label className="text-xs">{t('friend.label')}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={isLoading ? t('shared.loading') : t('campaign.choose')} /></SelectTrigger>
        <SelectContent>{conDung.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {x.status === 'running' ? t('campaign.running') : x.status === 'draft' ? t('campaign.draft') : x.status}</SelectItem>)}</SelectContent>
      </Select>
      {data && conDung.length === 0 && <p className="text-[11px] text-muted-foreground">{t('friend.none')}</p>}
      {c && c.status !== 'running' && <p className="text-[11px] text-warning">{t('friend.notRunning')}</p>}
      <p className="text-[11px] text-muted-foreground">{t('friend.hint')}</p>
    </div>
  )
}

function ChonChienDichNhanTin({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('autoConfig')
  const { data, isLoading } = useChienDichNhanTin()
  const conDung = (data ?? []).filter((c) => (c.status !== 'completed' && c.status !== 'cancelled') || c.id === value)
  const c = data?.find((x) => x.id === value)
  return (
    <div className="space-y-1">
      <Label className="text-xs">{t('broadcast.label')}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={isLoading ? t('shared.loading') : t('campaign.choose')} /></SelectTrigger>
        <SelectContent>{conDung.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {x.status === 'running' ? t('campaign.running') : x.status === 'draft' ? t('campaign.draft') : x.status}</SelectItem>)}</SelectContent>
      </Select>
      {data && conDung.length === 0 && <p className="text-[11px] text-muted-foreground">{t('broadcast.none')}</p>}
      {c && c.status !== 'running' && <p className="text-[11px] text-warning">{t('broadcast.notRunning')}</p>}
      <p className="text-[11px] text-muted-foreground">{t('broadcast.hint')}</p>
    </div>
  )
}

function ChonNhieuNhanVien({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation('autoConfig')
  const { data: members } = useOrgMembers()
  return (
    <div className="space-y-1">
      <Label className="text-xs">{t('staff.recipients', { count: value.length })}</Label>
      <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border p-1.5">
        {(members ?? []).map((m) => (
          <label key={m.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-accent/50">
            <input type="checkbox" className="h-3.5 w-3.5" checked={value.includes(m.id)}
              onChange={(e) => onChange(e.target.checked ? [...value, m.id] : value.filter((x) => x !== m.id))} />
            {m.fullName ?? m.id}
          </label>
        ))}
      </div>
    </div>
  )
}

function ChonThuocTinh({ value, onChange, chiSo }: { value: string; onChange: (v: string) => void; chiSo?: boolean }) {
  const { t } = useTranslation('autoConfig')
  const { data } = useCdpProperties()
  const props = (data?.properties ?? []).filter((p) => !chiSo || p.fieldType === 'number')
  const coTrongDs = props.some((p) => p.fieldKey === value)
  return (
    <div><Label className="text-xs">{t('prop.label')}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('prop.choose')} /></SelectTrigger>
        <SelectContent>
          {value && !coTrongDs && <SelectItem value={value}>{t('prop.notFound', { value })}</SelectItem>}
          {props.map((p) => <SelectItem key={p.id} value={p.fieldKey}><span className="font-mono">{p.fieldKey}</span> <span className="text-muted-foreground">— {p.name}</span></SelectItem>)}
        </SelectContent>
      </Select>
      {data && props.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">{chiSo ? t('prop.noneNumber') : t('prop.none')}</p>}
    </div>
  )
}

/** ZNS: chọn OA → chọn mẫu đã duyệt → điền tham số (có biến) → templateData. */
function CauHinhZns({ cfg, set, bien }: { cfg: Cfg; set: SetCfg; bien: TruongDuLieu[] }) {
  const { t } = useTranslation('autoConfig')
  const accountId = String(cfg.accountId ?? '')
  const templateId = String(cfg.templateId ?? '')
  const data = (cfg.templateData as Record<string, string> | undefined) ?? {}
  const { data: accounts } = useOaAccounts()
  const { data: templates, isLoading } = useZnsTemplates(accountId || undefined)
  const tpl = templates?.find((x) => x.templateId === templateId)
  const params = tpl?.params?.map((p) => p.name) ?? Object.keys(data)
  return (
    <>
      <div><Label className="text-xs">{t('zns.oa')}</Label>
        <Select value={accountId} onValueChange={(v) => set({ accountId: v, templateId: '', templateData: {} })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t('zns.chooseOa')} /></SelectTrigger>
          <SelectContent>{(accounts ?? []).map((a) => <SelectItem key={a.id} value={a.id}>{a.displayName ?? a.id}</SelectItem>)}</SelectContent>
        </Select>
        {accounts && accounts.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">{t('zns.noOa')}</p>}
      </div>
      {accountId && (
        <div><Label className="text-xs">{t('zns.template')}</Label>
          <Select value={templateId} onValueChange={(v) => {
            const moi = templates?.find((x) => x.templateId === v)
            set({ templateId: v, templateData: Object.fromEntries((moi?.params ?? []).map((p) => [p.name, data[p.name] ?? ''])) })
          }}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={isLoading ? t('shared.loading') : t('zns.chooseTemplate')} /></SelectTrigger>
            <SelectContent>{(templates ?? []).filter((x) => x.status === 'ENABLE' || x.templateId === templateId).map((x) => <SelectItem key={x.templateId} value={x.templateId}>{x.templateName}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {templateId && params.length > 0 && (
        <div className="space-y-1.5 rounded-md border p-2">
          <Label className="text-xs">{t('zns.params')}</Label>
          {params.map((p) => (
            <div key={p}>
              <p className="font-mono text-[10.5px] text-muted-foreground">{p}{tpl?.params?.find((x) => x.name === p)?.require ? ' *' : ''}</p>
              <Input value={data[p] ?? ''} onChange={(e) => set({ templateData: { ...data, [p]: e.target.value } })} className="h-8 text-xs" placeholder={`{{${bien[0]?.path ?? 'contact.fullName'}}}`} list="zns-bien" />
            </div>
          ))}
          <datalist id="zns-bien">{bien.map((b) => <option key={b.path} value={`{{${b.path}}}`}>{b.label}</option>)}</datalist>
          <p className="text-[10.5px] text-muted-foreground">{t('zns.hint', { example: '{{…}}' })}</p>
        </div>
      )}
    </>
  )
}
